import types from '../mutation-types';

const MAX_CACHED_CONVERSATIONS = 50;

const state = {
  // Cache for AISuggestionPanel (auto-suggestions in reply area)
  // Key: conversationId, Value: { suggestion, suggestionSource, lastMessageId, timestamp }
  suggestions: {},

  // Cache for AIAssistanceModal (manual AI assist modal)
  // Key: `${conversationId}-${type}-${contentHash}`, Value: { content, timestamp }
  assistanceResults: {},
};

export const getters = {
  getSuggestion: _state => conversationId => {
    return _state.suggestions[conversationId] || null;
  },
  getAssistanceResult: _state => cacheKey => {
    return _state.assistanceResults[cacheKey] || null;
  },
};

export const actions = {
  setSuggestion: (
    { commit },
    { conversationId, suggestion, suggestionSource, lastMessageId }
  ) => {
    commit(types.SET_AI_SUGGESTION_CACHE, {
      conversationId,
      data: {
        suggestion,
        suggestionSource,
        lastMessageId,
        timestamp: Date.now(),
      },
    });
  },
  invalidateSuggestion: ({ commit }, { conversationId }) => {
    commit(types.REMOVE_AI_SUGGESTION_CACHE, { conversationId });
  },
  setAssistanceResult: ({ commit }, { cacheKey, content }) => {
    commit(types.SET_AI_ASSISTANCE_CACHE, {
      cacheKey,
      data: { content, timestamp: Date.now() },
    });
  },
  invalidateAll: ({ commit }) => {
    commit(types.CLEAR_AI_SUGGESTION_CACHE);
  },
};

export const mutations = {
  [types.SET_AI_SUGGESTION_CACHE]($state, { conversationId, data }) {
    $state.suggestions = {
      ...$state.suggestions,
      [conversationId]: data,
    };
    // Evict oldest entries if cache too large
    const keys = Object.keys($state.suggestions);
    if (keys.length > MAX_CACHED_CONVERSATIONS) {
      const sorted = keys.sort(
        (a, b) =>
          ($state.suggestions[a]?.timestamp || 0) -
          ($state.suggestions[b]?.timestamp || 0)
      );
      const toRemove = sorted.slice(0, keys.length - MAX_CACHED_CONVERSATIONS);
      toRemove.forEach(key => {
        const { [key]: _, ...rest } = $state.suggestions;
        $state.suggestions = rest;
      });
    }
  },
  [types.REMOVE_AI_SUGGESTION_CACHE]($state, { conversationId }) {
    const { [conversationId]: _, ...rest } = $state.suggestions;
    $state.suggestions = rest;
  },
  [types.SET_AI_ASSISTANCE_CACHE]($state, { cacheKey, data }) {
    $state.assistanceResults = {
      ...$state.assistanceResults,
      [cacheKey]: data,
    };
    // Evict oldest entries if cache too large
    const keys = Object.keys($state.assistanceResults);
    if (keys.length > MAX_CACHED_CONVERSATIONS) {
      const sorted = keys.sort(
        (a, b) =>
          ($state.assistanceResults[a]?.timestamp || 0) -
          ($state.assistanceResults[b]?.timestamp || 0)
      );
      const toRemove = sorted.slice(0, keys.length - MAX_CACHED_CONVERSATIONS);
      toRemove.forEach(key => {
        const { [key]: _, ...rest } = $state.assistanceResults;
        $state.assistanceResults = rest;
      });
    }
  },
  [types.CLEAR_AI_SUGGESTION_CACHE]($state) {
    $state.suggestions = {};
    $state.assistanceResults = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
